import { Injectable } from '@angular/core';
import { select } from '@ngneat/elf';
import { deleteEntities, selectAllEntities, setEntities, upsertEntities } from '@ngneat/elf-entities';
import { concat, defer, distinctUntilChanged, EMPTY, merge, Observable, of, shareReplay, Subject, switchMap, timer } from 'rxjs';

import { effectsStore } from './effects.store';
import { PriceSyncStatus } from '~shared/types';
import { ChangeStatusData, EffectStatusData } from '~shared/types/websockets.types';

/** The BOM rebuild that runs in the configurator; registered first on a price-impacting change. */
const UPDATE_BOMITEMS = 'UPDATE_BOMITEMS';
/** The Odoo price recalculation, which is what actually moves the price the UI shows. */
const SYNC_BOM_ODOO = 'SYNC_BOM_ODOO';

/**
 * How long a change may go without any of its effects reporting in before its outstanding
 * price work is treated as stuck. Measured against local time from the last report this
 * session saw, so a long rebuild that keeps reporting items back never trips it, while an
 * effect abandoned mid-flight stops holding the price hostage.
 */
const STALLED_AFTER_MS = 90 * 1000;

/**
 * How long a change whose rebuilds have all landed keeps the placeholder up while waiting
 * for the recalculation they should trigger. Long enough to bridge the registration gap,
 * short enough that a change which never gets one — they are not registered for every
 * change — releases the price rather than waiting on something that is not coming.
 */
const AWAITING_RECALC_MS = 10 * 1000;

@Injectable({ providedIn: 'root' })
export class EffectsRepository {
	changes$ = effectsStore.pipe(selectAllEntities());
	loaded$ = effectsStore.pipe(select(state => state.loaded));

	/** When this session last saw price work of a change report in, keyed by correlationId. */
	private readonly lastActivityAt = new Map<string, number>();
	/** When this session last saw any other work of a change report in, keyed by correlationId. */
	private readonly lastOtherActivityAt = new Map<string, number>();
	/** When this session last saw a recalculation settle, whichever change it belonged to. */
	private lastConfirmedAt = 0;
	/** Stalled changes already asked about, so one silence asks the backend once. */
	private readonly reconcileRequests = new Set<string>();
	private readonly stalled$ = new Subject<void>();

	/**
	 * Fires when price work has gone quiet for long enough that this session's picture of it
	 * needs checking against the backend. Whoever owns the change feed answers it by pushing
	 * a fresh set of pending changes in; the store itself does no fetching.
	 */
	reconcileNeeded$ = merge(
		this.stalled$,
		this.changes$.pipe(switchMap(changes => this.silentOtherWork$(changes))),
	);

	/**
	 * Whether the price on screen is the final one. The rebuild counts as price work
	 * alongside the recalculation it precedes: watching the recalculation alone leaves the
	 * stale price standing for the whole rebuild, which reads as "no price impact". Odoo
	 * prices the project as a whole, so this is one project-wide answer rather than one
	 * per part — work outstanding anywhere puts every price on the page in doubt.
	 */
	priceStatus$ = this.changes$.pipe(
		switchMap(changes => this.statusFor$(changes)),
		distinctUntilChanged(),
		shareReplay(1),
	);

	setChanges(changes: ChangeStatusData[]): void {
		effectsStore.update(setEntities(changes), (state) => ({ ...state, loaded: true }));
	}

	upsertEffect(effect: EffectStatusData): void {
		this.recordActivity(effect);
		const current = effectsStore.getValue().entities;
		const changeId = effect.correlationId;
		const existing = current[changeId];
		if (existing) {
			const effects = existing.effects.filter(e => e.effectId !== effect.effectId);
			effects.push(effect);
			effectsStore.update(upsertEntities({ ...existing, effects }));
		} else {
			effectsStore.update(upsertEntities({
				correlationId: effect.correlationId,
				originator: effect.registeredBy,
				createdAt: effect.registeredAt,
				effects: [effect],
			}));
		}
	}

	hasChange(correlationId: string): boolean {
		return !!effectsStore.getValue().entities[correlationId];
	}

	removeChange(correlationId: string): void {
		this.lastActivityAt.delete(correlationId);
		this.lastOtherActivityAt.delete(correlationId);
		effectsStore.update(deleteEntities(correlationId));
	}

	removeAllChanges(): void {
		this.forgetActivity();
		effectsStore.update(setEntities([]));
	}

	reset(): void {
		this.forgetActivity();
		effectsStore.reset();
	}

	/**
	 * The image and detail syncs go quiet the same way price work does, and a missed report
	 * leaves them pending on the badge until a reload. They never bear on the price, so they
	 * are watched apart from it: once one has been silent for as long as stalled price work,
	 * the backend is asked once, and asked again only after the change shows new activity.
	 */
	private silentOtherWork$(changes: ChangeStatusData[]): Observable<void> {
		return defer(() => {
			const now = Date.now();
			const waiting = changes.filter(change => change.effects.some(e => !isPriceEffect(e) && isOutstanding(e)));
			const silent = waiting.filter(change => this.otherActivityOf(change) + STALLED_AFTER_MS <= now);
			const asks = silent
				.map(change => `${change.correlationId}:other:${this.otherActivityOf(change)}`)
				.filter(request => !this.reconcileRequests.has(request));
			asks.forEach(request => this.reconcileRequests.add(request));
			const deadlines = waiting
				.map(change => this.otherActivityOf(change) + STALLED_AFTER_MS)
				.filter(deadline => deadline > now);
			const next$ = deadlines.length
				? timer(Math.min(...deadlines) - now).pipe(switchMap(() => this.silentOtherWork$(changes)))
				: EMPTY;
			return asks.length ? concat(of(undefined), next$) : next$;
		});
	}

	/**
	 * Status for one set of changes, re-emitted when a change falls silent long enough to
	 * count as stalled, so price work that is never reported back resolves on its own.
	 */
	private statusFor$(changes: ChangeStatusData[]): Observable<PriceSyncStatus> {
		return defer(() => {
			const now = Date.now();
			const status = this.statusOf(changes, now);
			this.requestReconcile(changes, now);
			const nextDeadline = this.msUntilNextDeadline(changes, now);
			return nextDeadline === null
				? of(status)
				: concat(of(status), timer(nextDeadline).pipe(switchMap(() => this.statusFor$(changes))));
		});
	}

	/**
	 * Effects that go quiet are as often a report this session missed as work that is truly
	 * stuck, and this session cannot tell the two apart. Asking the backend for the pending
	 * changes settles it: a change it no longer lists drops out and its prices are released,
	 * which is what makes reloading the page fix a stuck placeholder today. Work that
	 * reported a failure is not silence — it is an answer — so it is not asked about again.
	 */
	private requestReconcile(changes: ChangeStatusData[], now: number): void {
		changes
			.filter(change => this.statusOfChange(change, now) === 'unconfirmed' && !hasFailedPriceEffect(change))
			.forEach((change) => {
				const request = `${change.correlationId}:${this.activityOf(change)}`;
				if (!this.reconcileRequests.has(request)) {
					this.reconcileRequests.add(request);
					this.stalled$.next();
				}
			});
	}

	/** Work in flight wins over stalled work: it is going to produce a number either way. */
	private statusOf(changes: ChangeStatusData[], now: number): PriceSyncStatus {
		const statuses = changes.map(change => this.statusOfChange(change, now));
		if (statuses.includes('syncing')) {
			return 'syncing';
		}
		return statuses.includes('unconfirmed') ? 'unconfirmed' : 'settled';
	}

	private statusOfChange(change: ChangeStatusData, now: number): PriceSyncStatus {
		const priceEffects = change.effects.filter(isPriceEffect);
		if (!priceEffects.length) {
			return 'settled';
		}
		// A rebuild that failed leaves the bomitems behind the design they were meant to
		// follow. Nothing downstream repairs that — Odoo prices whatever the bomitems say, so
		// a recalculation on top of them is confidently wrong. The price stays in doubt until
		// the rebuild is run again and the change cleared, however many recalculations land.
		if (priceEffects.some(e => e.effectType === UPDATE_BOMITEMS && e.status === 'FAILED')) {
			return 'unconfirmed';
		}
		const activeAt = this.activityOf(change);
		const recalcs = priceEffects.filter(e => e.effectType === SYNC_BOM_ODOO);
		// A recalculation that failed left the project priced as it stood before this change.
		// Odoo prices the project as a whole, so a recalculation that landed since covers this
		// change as well and settles it; without one, the number on screen predates the change.
		// A retry against this same change is such a recalculation, and it reports in at the
		// moment the change last showed activity, which no comparison of the two can separate.
		if (recalcs.some(e => e.status === 'FAILED') && !recalcs.some(e => e.status === 'COMPLETE')) {
			return this.lastConfirmedAt > activeAt ? 'settled' : 'unconfirmed';
		}
		// A settled recalculation means this change's price impact has landed — even if one of
		// its rebuilds is left behind in PENDING, as orphaned rebuilds have been seen to do.
		// The rebuild cannot move the price on its own, so there is nothing left to wait for.
		if (recalcs.length && !recalcs.some(isOutstanding)) {
			return 'settled';
		}
		if (!priceEffects.some(isOutstanding)) {
			// The rebuilds all landed and no recalculation is registered against this change.
			// One usually follows within seconds, but plenty of changes never get one, so this
			// holds the placeholder just long enough to bridge that gap and then lets go.
			return activeAt + AWAITING_RECALC_MS > now ? 'syncing' : 'settled';
		}
		// Work is genuinely outstanding: a recalculation running, or a rebuild still going.
		if (activeAt + STALLED_AFTER_MS > now) {
			return 'syncing';
		}
		// Nothing reported back in time. A recalculation that settled since then priced the
		// whole project, this change included, so it makes the stalled work moot.
		return this.lastConfirmedAt > activeAt ? 'settled' : 'unconfirmed';
	}

	/** Milliseconds until a change's status turns over on its own, or null when none will. */
	private msUntilNextDeadline(changes: ChangeStatusData[], now: number): number | null {
		const deadlines = changes
			.filter(change => this.statusOfChange(change, now) === 'syncing')
			.map((change) => {
				const outstanding = change.effects.filter(isPriceEffect).some(isOutstanding);
				return this.activityOf(change) + (outstanding ? STALLED_AFTER_MS : AWAITING_RECALC_MS);
			})
			.filter(deadline => deadline > now);
		return deadlines.length ? Math.min(...deadlines) - now : null;
	}

	/** When the change's other work last showed a sign of life, stamped the first time it is seen. */
	private otherActivityOf(change: ChangeStatusData): number {
		let activeAt = this.lastOtherActivityAt.get(change.correlationId);
		if (activeAt === undefined) {
			activeAt = Date.now();
			this.lastOtherActivityAt.set(change.correlationId, activeAt);
		}
		return activeAt;
	}

	/** When the change last showed a sign of life, stamped the first time it is seen. */
	private activityOf(change: ChangeStatusData): number {
		let activeAt = this.lastActivityAt.get(change.correlationId);
		if (activeAt === undefined) {
			activeAt = Date.now();
			this.lastActivityAt.set(change.correlationId, activeAt);
		}
		return activeAt;
	}

	/**
	 * Price effects arriving over the websocket are what keep a change from counting as
	 * stalled. Only they count: the image and detail syncs of the same change carry on well
	 * after its pricing is decided, and letting one of those stamp the change would re-open a
	 * question its price work has already answered.
	 */
	private recordActivity(effect: EffectStatusData): void {
		if (!isPriceEffect(effect)) {
			this.lastOtherActivityAt.set(effect.correlationId, Date.now());
			return;
		}
		const now = Date.now();
		this.lastActivityAt.set(effect.correlationId, now);
		if (effect.effectType === SYNC_BOM_ODOO && !isOutstanding(effect)) {
			this.lastConfirmedAt = now;
		}
	}

	private forgetActivity(): void {
		this.lastActivityAt.clear();
		this.lastOtherActivityAt.clear();
		this.reconcileRequests.clear();
		this.lastConfirmedAt = 0;
	}
}

/** Still running. A failure is not outstanding — it is finished, with a bad outcome. */
function isOutstanding(effect: EffectStatusData): boolean {
	return effect.status === 'PENDING' || effect.status === 'IN_PROGRESS';
}

/** Whether price work of this change reported back a failure rather than a result. */
function hasFailedPriceEffect(change: ChangeStatusData): boolean {
	return change.effects.some(effect => isPriceEffect(effect) && effect.status === 'FAILED');
}

/** The image and detail syncs run alongside these, but only these two bear on the price. */
function isPriceEffect(effect: EffectStatusData): boolean {
	return effect.effectType === SYNC_BOM_ODOO || effect.effectType === UPDATE_BOMITEMS;
}
